import { InteractionRequiredAuthError } from '@azure/msal-browser'
import { msalInstance, loginRequest } from './auth'
import { DEV_AUTH_BYPASS } from './devAuth'

const API_BASE = import.meta.env.VITE_API_URL as string ?? 'http://localhost:4000'

async function getAccessToken(): Promise<string> {
  const accounts = msalInstance.getAllAccounts()
  if (accounts.length === 0) {
    await msalInstance.acquireTokenRedirect(loginRequest)
    throw new Error('Redirecting to login')
  }

  try {
    const response = await msalInstance.acquireTokenSilent({
      ...loginRequest,
      account: accounts[0]!,
    })
    return response.accessToken
  } catch (err) {
    // Refresh token expired or interaction required — redirect to Entra login
    if (err instanceof InteractionRequiredAuthError) {
      await msalInstance.acquireTokenRedirect({ ...loginRequest, account: accounts[0]! })
      throw new Error('Redirecting to login')
    }
    throw err
  }
}

interface FetchOptions extends Omit<RequestInit, 'headers'> {
  headers?: Record<string, string>
}

/** The Authorization header for API calls; none in local dev without Entra (ADR-017). */
export async function authHeaders(): Promise<Record<string, string>> {
  if (DEV_AUTH_BYPASS) return {}
  return { Authorization: `Bearer ${await getAccessToken()}` }
}

export async function apiFetch<T>(path: string, options: FetchOptions = {}): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      ...(options.body !== undefined && { 'Content-Type': 'application/json' }),
      ...await authHeaders(),
      ...options.headers,
    },
  })

  if (response.status === 401 && DEV_AUTH_BYPASS) {
    throw new Error('The API rejected local sign-in: set DEV_AUTH_BYPASS=true for the API too (ADR-017)')
  }

  if (response.status === 401) {
    // Token was rejected server-side — force a fresh login
    await msalInstance.acquireTokenRedirect(loginRequest)
    throw new Error('Redirecting to login')
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' })) as { message?: string }
    throw new Error(error.message ?? `HTTP ${response.status}`)
  }

  if (response.status === 204 || response.status === 205) {
    return undefined as T
  }

  return response.json() as Promise<T>
}
